/**
 * Group Homes Management — Modals + PostShiftForm
 * Extracted from GroupHomesManagement.js on Feb 20, 2026 as part of the
 * 3.9k-line component decomposition.
 * Owns: PostShiftForm, CreateProgramModal, CreateLocationModal,
 *       CreateShiftModal, AssignManagerModal, CreatePmAccountModal,
 *       BulkImportPmModal.
 */
import React, { useState, useEffect, useRef } from 'react';
import { apiCall } from '../../../utils/api';
import { validators, validateTimeRange } from '../../../utils/validation';
import { truncateLabel } from '../../../utils/text';
import { toast } from '../../ui/sonner';
import PayRateGate from '../../shared/PayRateGate';
import {
  AlertCircle, Camera, CheckCircle, ClipboardList, Download, Eye, FileText, Mail,
  MapPin, Pen, Plus, RefreshCw, Trash2, Type, Upload, X
} from 'lucide-react';
import CreateShiftModal from './CreateShiftModal';
import AddressAutocomplete from '../../AddressAutocomplete';

function PostShiftForm({ locationId, locationName, programId, onSuccess, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    date: '',
    start_time: '09:00',
    end_time: '17:00',
    hourly_rate: 25,
    required_workers: 1,
    category: 'healthcare',
    required_skills: []
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.date) {
      toast.error('Please fill in the shift title and date');
      return;
    }
    const rangeErr = validateTimeRange(`${form.date}T${form.start_time}`, `${form.date}T${form.end_time}`, 'Shift end time');
    if (rangeErr) {
      toast.error(rangeErr);
      return;
    }
    
    setLoading(true);
    try {
      await apiCall('/api/employer/group-homes/shifts', {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          location_id: locationId,
          program_id: programId,
          status: 'open'
        })
      });
      onSuccess();
    } catch (error) {
      toast.error(error.message || 'Failed to post shift');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
        <p className="text-sm text-slate-500">Posting shift for:</p>
        <p className="font-semibold text-slate-800 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-emerald-600" />
          {locationName}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Shift Title *</label>
        <input
          type="text"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="e.g., Night Shift Caregiver"
          className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
        <textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Describe the shift responsibilities..."
          rows={3}
          className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Date *</label>
          <input
            type="date"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            min={new Date().toISOString().split('T')[0]}
            className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Hourly Rate ($)</label>
          <PayRateGate>
          <input
            type="number"
            value={form.hourly_rate}
            onChange={(e) => setForm({ ...form, hourly_rate: parseFloat(e.target.value) || 0 })}
            min="0"
            step="0.5"
            className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
          />
          </PayRateGate>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Start Time</label>
          <input
            type="time"
            value={form.start_time}
            onChange={(e) => setForm({ ...form, start_time: e.target.value })}
            className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">End Time</label>
          <input
            type="time"
            value={form.end_time}
            onChange={(e) => setForm({ ...form, end_time: e.target.value })}
            className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Workers Needed</label>
        <input
          type="number"
          value={form.required_workers}
          onChange={(e) => setForm({ ...form, required_workers: parseInt(e.target.value) || 1 })}
          min="1"
          className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
        />
      </div>

      <div className="flex gap-3 pt-4 border-t border-slate-200">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-50 transition"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex-1 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-medium hover:shadow-lg transition disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Posting...
            </>
          ) : (
            <>
              <Plus className="w-4 h-4" />
              Post Shift
            </>
          )}
        </button>
      </div>
    </form>
  );
}

// Modal Components
function CreateProgramModal({ onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    service_type: 'residential_care',
    max_locations: 20,
    contact_email: '',
    contact_phone: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const vErr =
      validators.required(form.name) ||
      validators.companyName(form.name) ||
      validators.email(form.contact_email) ||
      validators.phone(form.contact_phone);
    if (vErr) {
      toast.error(vErr);
      return;
    }
    setLoading(true);
    try {
      await apiCall('/api/employer/group-homes/programs', {
        method: 'POST',
        body: JSON.stringify(form)
      });
      toast.success('Program created successfully!');
      onCreated();
    } catch (error) {
      const msg = error.message || '';
      if (msg.includes('already exists')) {
        if (window.confirm(`${msg}\n\nCreate anyway?`)) {
          try {
            await apiCall('/api/employer/group-homes/programs', {
              method: 'POST',
              body: JSON.stringify({ ...form, allow_duplicate: true })
            });
            toast.success('Program created successfully!');
            onCreated();
          } catch (e2) {
            toast.error(e2.message || 'Failed to create program');
          }
        }
      } else {
        toast.error(msg || 'Failed to create program');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-xl font-bold text-slate-800">Create New Program</h3>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Program Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              maxLength={60}
              className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-indigo-500"
              placeholder="e.g., Adult Residential Care"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Service Type *</label>
            <select
              value={form.service_type}
              onChange={(e) => setForm({ ...form, service_type: e.target.value })}
              className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-indigo-500"
            >
              <option value="residential_care">Residential Care</option>
              <option value="disability_support">Disability Support</option>
              <option value="behavioral_health">Behavioral Health</option>
              <option value="elderly_care">Elderly Care</option>
              <option value="rehabilitation">Rehabilitation</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-indigo-500 resize-none"
              rows={3}
              placeholder="Describe the program..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Max Locations</label>
              <input
                type="number"
                value={form.max_locations}
                onChange={(e) => setForm({ ...form, max_locations: parseInt(e.target.value) })}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-indigo-500"
                min="1"
                max="50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Contact Phone</label>
              <input
                type="tel"
                value={form.contact_phone}
                onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-indigo-500"
                placeholder="(555) 123-4567"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-slate-600 hover:bg-slate-100 rounded-xl transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition disabled:opacity-50 flex items-center gap-2"
            >
              {loading && <RefreshCw className="w-4 h-4 animate-spin" />}
              Create Program
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CreateLocationModal({ program, onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    capacity: 10,
    address: { street: '', city: '', state: '', zip: '' },
    contact_phone: '',
    contact_email: '',
    certifications_required: []
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const vErr =
      validators.required(form.name) ||
      validators.companyName(form.name) ||
      validators.wholeNumber(form.capacity) ||
      (Number(form.capacity) <= 0 ? 'Capacity must be greater than 0.' : null) ||
      (!/\d/.test(form.address.street) || !/[a-zA-Z].*[a-zA-Z].*[a-zA-Z]/.test(form.address.street)
        ? 'Please enter a real street address (e.g., 123 Main St) — pick one from the suggestions.'
        : null) ||
      (form.address.zip && !/^\d{5}(-\d{4})?$/.test(form.address.zip.trim()) ? 'ZIP code must be 5 digits.' : null) ||
      (form.address.city ? validators.city(form.address.city) : null) ||
      validators.email(form.contact_email) ||
      validators.phone(form.contact_phone);
    if (vErr) {
      toast.error(vErr);
      return;
    }
    setLoading(true);
    try {
      await apiCall(`/api/employer/group-homes/programs/${program._id}/locations`, {
        method: 'POST',
        body: JSON.stringify(form)
      });
      toast.success('Location created successfully!');
      onCreated();
    } catch (error) {
      const msg = error.message || '';
      if (msg.includes('already exists')) {
        if (window.confirm(`${msg}\n\nCreate anyway?`)) {
          try {
            await apiCall(`/api/employer/group-homes/programs/${program._id}/locations`, {
              method: 'POST',
              body: JSON.stringify({ ...form, allow_duplicate: true })
            });
            toast.success('Location created successfully!');
            onCreated();
          } catch (e2) {
            toast.error(e2.message || 'Failed to create location');
          }
        }
      } else {
        toast.error(msg || 'Failed to create location');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 sticky top-0 bg-white">
          <h3 className="text-xl font-bold text-slate-800">Add Group Home</h3>
          <p className="text-sm text-slate-500">Under: {program.name}</p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Location Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              maxLength={60}
              className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-emerald-500"
              placeholder="e.g., Sunrise House"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Capacity (beds) *</label>
            <input
              type="number"
              value={form.capacity}
              onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) })}
              className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-emerald-500"
              min="1"
              max="100"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Address *</label>
            <div className="mb-2">
              <AddressAutocomplete
                value={form.address.street}
                onChange={(text) => setForm((prev) => ({ ...prev, address: { ...prev.address, street: text } }))}
                onSelect={(s) => setForm((prev) => ({
                  ...prev,
                  address: {
                    street: s.street || s.label,
                    city: s.city || prev.address.city,
                    state: s.state || prev.address.state,
                    zip: s.zip || prev.address.zip,
                  },
                }))}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-emerald-500"
                placeholder="Start typing street address…"
                required
                testId="create-location-address"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <input
                type="text"
                value={form.address.city}
                onChange={(e) => setForm({ ...form, address: { ...form.address, city: e.target.value } })}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-emerald-500"
                placeholder="City"
                required
              />
              <input
                type="text"
                value={form.address.state}
                onChange={(e) => setForm({ ...form, address: { ...form.address, state: e.target.value } })}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-emerald-500"
                placeholder="State"
                required
              />
              <input
                type="text"
                value={form.address.zip}
                onChange={(e) => setForm({ ...form, address: { ...form.address, zip: e.target.value } })}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-emerald-500"
                placeholder="ZIP"
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Contact Phone</label>
              <input
                type="tel"
                value={form.contact_phone}
                onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-emerald-500"
                placeholder="(555) 123-4567"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Contact Email</label>
              <input
                type="email"
                value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-emerald-500"
                placeholder="contact@example.com"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-slate-600 hover:bg-slate-100 rounded-xl transition">
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition disabled:opacity-50 flex items-center gap-2"
            >
              {loading && <RefreshCw className="w-4 h-4 animate-spin" />}
              Add Location
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Assign Manager Modal
function AssignManagerModal({ programs, onClose, onAssigned }) {
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({
    user_email: '',
    role: 'program_manager',
    program_id: '',
    location_id: ''
  });

  // Load locations when program changes
  useEffect(() => {
    if (form.program_id) {
      loadLocations(form.program_id);
    } else {
      setLocations([]);
      setForm(prev => ({ ...prev, location_id: '' }));
    }
  }, [form.program_id]);

  const loadLocations = async (programId) => {
    try {
      const response = await apiCall(`/api/employer/group-homes/programs/${programId}/locations`);
      setLocations(response.locations || []);
    } catch (error) {
      console.error('Failed to load locations:', error);
    }
  };

  const searchUsers = async (email) => {
    if (email.length < 3) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const response = await apiCall(`/api/employer/search-users?email=${encodeURIComponent(email)}`);
      setSearchResults(response.users || []);
    } catch (error) {
      console.error('Failed to search users:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser) {
      toast.error('Please select a user to assign');
      return;
    }
    setLoading(true);
    try {
      await apiCall('/api/employer/group-homes/managers', {
        method: 'POST',
        body: JSON.stringify({
          user_id: selectedUser._id,
          role: form.role,
          program_id: form.program_id || null,
          location_id: form.location_id || null
        })
      });
      toast.success('Manager assigned successfully!');
      onAssigned();
    } catch (error) {
      toast.error(error.message || 'Failed to assign manager');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-purple-600 to-pink-600 rounded-t-2xl">
          <h3 className="text-xl font-bold text-white">Assign Manager</h3>
          <p className="text-sm text-white/80">Delegate program or location management</p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* User Search */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Search User by Email *</label>
            <div className="relative">
              <input
                type="email"
                value={form.user_email}
                onChange={(e) => {
                  setForm({ ...form, user_email: e.target.value });
                  searchUsers(e.target.value);
                }}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-purple-500"
                placeholder="Start typing email..."
                required
              />
              {searchLoading && (
                <RefreshCw className="absolute right-3 top-3 w-5 h-5 animate-spin text-slate-400" />
              )}
            </div>
            {/* Search Results Dropdown */}
            {searchResults.length > 0 && !selectedUser && (
              <div className="mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                {searchResults.map(user => (
                  <button
                    key={user._id}
                    type="button"
                    onClick={() => {
                      setSelectedUser(user);
                      setForm({ ...form, user_email: user.email });
                      setSearchResults([]);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center gap-3 border-b last:border-0"
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                      {user.full_name?.charAt(0) || 'U'}
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">{user.full_name}</p>
                      <p className="text-sm text-slate-500">{user.email}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {/* Selected User Display */}
            {selectedUser && (
              <div className="mt-2 p-3 bg-purple-50 rounded-xl flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                    {selectedUser.full_name?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">{selectedUser.full_name}</p>
                    <p className="text-sm text-slate-500">{selectedUser.email}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedUser(null);
                    setForm({ ...form, user_email: '' });
                  }}
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>

          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Manager Role *</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value, location_id: '' })}
              className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-purple-500"
            >
              <option value="program_manager">Program Manager</option>
              <option value="location_manager">Location Manager</option>
            </select>
            <p className="text-xs text-slate-500 mt-1">
              {form.role === 'program_manager' 
                ? 'Can manage all locations within a program' 
                : 'Can only manage a specific location'}
            </p>
          </div>

          {/* Program Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Assign to Program *</label>
            <select
              value={form.program_id}
              onChange={(e) => setForm({ ...form, program_id: e.target.value })}
              className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-purple-500"
              required
            >
              <option value="">Select a program...</option>
              {programs.map(program => (
                <option key={program._id} value={program._id}>{program.name}</option>
              ))}
            </select>
          </div>

          {/* Location Selection (for Location Managers) */}
          {form.role === 'location_manager' && form.program_id && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Assign to Location *</label>
              <select
                value={form.location_id}
                onChange={(e) => setForm({ ...form, location_id: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-purple-500"
                required
              >
                <option value="">Select a location...</option>
                {locations.map(location => (
                  <option key={location._id} value={location._id} title={location.name}>{truncateLabel(location.name, 48)}</option>
                ))}
              </select>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-slate-600 hover:bg-slate-100 rounded-xl transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !selectedUser}
              className="px-5 py-2.5 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition disabled:opacity-50 flex items-center gap-2"
            >
              {loading && <RefreshCw className="w-4 h-4 animate-spin" />}
              Assign Manager
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Create PM Account Modal
function CreatePmAccountModal({ programs, onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [createdAccount, setCreatedAccount] = useState(null);
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    password: '',
    auto_generate_password: true,
    role: 'program_manager',
    program_id: '',
    location_id: ''
  });

  // Load locations when program changes
  useEffect(() => {
    if (form.program_id) {
      loadLocations(form.program_id);
    } else {
      setLocations([]);
      setForm(prev => ({ ...prev, location_id: '' }));
    }
  }, [form.program_id]);

  const loadLocations = async (programId) => {
    try {
      const response = await apiCall(`/api/employer/group-homes/programs/${programId}/locations`);
      setLocations(response.locations || []);
    } catch (error) {
      console.error('Failed to load locations:', error);
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setForm({ ...form, password, auto_generate_password: false });
    setShowPassword(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        role: form.role,
        program_id: form.program_id,
        location_id: form.location_id || null
      };
      
      // Only include password if not auto-generating
      if (!form.auto_generate_password && form.password) {
        payload.password = form.password;
      }

      const response = await apiCall('/api/employer/group-homes/create-pm-account', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      
      setCreatedAccount(response);
      toast.success('PM account created successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to create PM account');
    } finally {
      setLoading(false);
    }
  };

  // Success screen after account creation
  if (createdAccount) {
    return (
      <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
          <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-t-2xl">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-white" />
              <div>
                <h3 className="text-xl font-bold text-white">Account Created!</h3>
                <p className="text-sm text-white/80">PM account has been set up successfully</p>
              </div>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <div className="bg-slate-50 rounded-xl p-4">
              <h4 className="font-semibold text-slate-800 mb-3">Account Details</h4>
              <div className="space-y-2 text-sm">
                <p><span className="text-slate-500">Name:</span> <span className="font-medium text-slate-800">{createdAccount.user.full_name}</span></p>
                <p><span className="text-slate-500">Email:</span> <span className="font-medium text-slate-800">{createdAccount.user.email}</span></p>
                <p><span className="text-slate-500">Role:</span> <span className="font-medium text-slate-800 capitalize">{createdAccount.assignment.role.replace('_', ' ')}</span></p>
                <p><span className="text-slate-500">Program:</span> <span className="font-medium text-slate-800">{createdAccount.assignment.program_name}</span></p>
              </div>
            </div>

            {/* Credentials Box */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <h4 className="font-semibold text-amber-800">Login Credentials</h4>
              </div>
              <div className="space-y-2 text-sm font-mono bg-white rounded-lg p-3 border border-amber-200">
                <p><span className="text-slate-500">Email:</span> <span className="font-bold text-slate-800">{createdAccount.credentials.email}</span></p>
                <p><span className="text-slate-500">Password:</span> <span className="font-bold text-slate-800">{createdAccount.credentials.password}</span></p>
              </div>
              <p className="text-xs text-amber-700 mt-2">
                {createdAccount.credentials.auto_generated 
                  ? '⚠️ Auto-generated password. Please share with the PM securely.'
                  : '⚠️ Please share these credentials with the PM securely.'}
              </p>
            </div>

            {/* Email Status */}
            <div className={`flex items-center gap-2 p-3 rounded-lg ${createdAccount.email_sent ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
              {createdAccount.email_sent ? (
                <>
                  <Mail className="w-5 h-5" />
                  <span className="text-sm">Welcome email sent to {createdAccount.user.email}</span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-5 h-5" />
                  <span className="text-sm">Email not sent - please share credentials manually</span>
                </>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => {
                  setCreatedAccount(null);
                  setForm({
                    full_name: '',
                    email: '',
                    phone: '',
                    password: '',
                    auto_generate_password: true,
                    role: 'program_manager',
                    program_id: '',
                    location_id: ''
                  });
                }}
                className="px-5 py-2.5 text-emerald-600 hover:bg-emerald-50 rounded-xl transition"
              >
                Create Another
              </button>
              <button
                onClick={onCreated}
                className="px-5 py-2.5 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-t-2xl">
          <h3 className="text-xl font-bold text-white">Create PM Account</h3>
          <p className="text-sm text-white/80">Create a new Program Manager with login credentials</p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
            <input
              type="text"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-emerald-500"
              placeholder="John Smith"
              required
              data-testid="pm-account-name"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-emerald-500"
              placeholder="pm@company.com"
              required
              data-testid="pm-account-email"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-emerald-500"
              placeholder="+1 (555) 123-4567"
              data-testid="pm-account-phone"
            />
          </div>

          {/* Password Options */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Password</label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="password_option"
                  checked={form.auto_generate_password}
                  onChange={() => setForm({ ...form, auto_generate_password: true, password: '' })}
                  className="w-4 h-4 text-emerald-600"
                />
                <span className="text-sm text-slate-700">Auto-generate password</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="password_option"
                  checked={!form.auto_generate_password}
                  onChange={() => setForm({ ...form, auto_generate_password: false })}
                  className="w-4 h-4 text-emerald-600"
                />
                <span className="text-sm text-slate-700">Set password manually</span>
              </label>
            </div>
            
            {!form.auto_generate_password && (
              <div className="mt-2 flex gap-2">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="flex-1 px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-emerald-500"
                  placeholder="Enter password"
                  minLength={8}
                  data-testid="pm-account-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="px-4 py-3 bg-slate-100 rounded-xl text-slate-600 hover:bg-slate-200 transition"
                >
                  <Eye className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={generatePassword}
                  className="px-4 py-3 bg-emerald-100 rounded-xl text-emerald-700 hover:bg-emerald-200 transition"
                  title="Generate strong password"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>

          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Manager Role *</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value, location_id: '' })}
              className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-emerald-500"
              required
              data-testid="pm-account-role"
            >
              <option value="program_manager">Program Manager</option>
              <option value="location_manager">Location Manager</option>
            </select>
          </div>

          {/* Program Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Assign to Program *</label>
            <select
              value={form.program_id}
              onChange={(e) => setForm({ ...form, program_id: e.target.value })}
              className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-emerald-500"
              required
              data-testid="pm-account-program"
            >
              <option value="">Select a program...</option>
              {programs.map(program => (
                <option key={program._id} value={program._id}>{program.name}</option>
              ))}
            </select>
          </div>

          {/* Location Selection (for Location Managers) */}
          {form.role === 'location_manager' && form.program_id && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Assign to Location *</label>
              <select
                value={form.location_id}
                onChange={(e) => setForm({ ...form, location_id: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-emerald-500"
                required
                data-testid="pm-account-location"
              >
                <option value="">Select a location...</option>
                {locations.map(location => (
                  <option key={location._id} value={location._id} title={location.name}>{truncateLabel(location.name, 48)}</option>
                ))}
              </select>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-slate-600 hover:bg-slate-100 rounded-xl transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-medium hover:shadow-lg transition disabled:opacity-50 flex items-center gap-2"
              data-testid="pm-account-submit"
            >
              {loading && <RefreshCw className="w-4 h-4 animate-spin" />}
              Create PM Account
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Bulk Import PM Modal
function BulkImportPmModal({ onClose, onImported }) {
  const [loading, setLoading] = useState(false);
  const [template, setTemplate] = useState(null);
  const [file, setFile] = useState(null);
  const [results, setResults] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadTemplate();
  }, []);

  const loadTemplate = async () => {
    try {
      const response = await apiCall('/api/employer/group-homes/pm-import-template');
      setTemplate(response.template);
    } catch (error) {
      console.error('Failed to load template:', error);
    }
  };

  const downloadTemplate = () => {
    if (!template) return;
    
    const headers = template.headers.join(',');
    const rows = template.example_rows.map(row => row.join(',')).join('\n');
    const csv = `${headers}\n${rows}`;
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pm_import_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Template downloaded!');
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.type === 'text/csv' || droppedFile.name.endsWith('.csv')) {
        setFile(droppedFile);
      } else {
        toast.error('Please upload a CSV file');
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Please select a CSV file');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const API_URL = process.env.REACT_APP_BACKEND_URL || '';

      // iter77 Phase B: cookie-based auth, no Bearer header needed.
      const response = await fetch(`${API_URL}/api/employer/group-homes/bulk-import-pm`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Import failed');
      }

      setResults(data.results);
      toast.success(data.message);
    } catch (error) {
      toast.error(error.message || 'Failed to import PM accounts');
    } finally {
      setLoading(false);
    }
  };

  // Results view
  if (results) {
    return (
      <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-t-2xl">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-white" />
              <div>
                <h3 className="text-xl font-bold text-white">Import Complete</h3>
                <p className="text-sm text-white/80">
                  Created {results.created} of {results.total} accounts
                </p>
              </div>
            </div>
          </div>
          
          <div className="p-6 space-y-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-3">
              <div className="bg-slate-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-slate-800">{results.total}</p>
                <p className="text-xs text-slate-500">Total Rows</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-emerald-600">{results.created}</p>
                <p className="text-xs text-emerald-600">Created</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-amber-600">{results.skipped}</p>
                <p className="text-xs text-amber-600">Skipped</p>
              </div>
              <div className="bg-red-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-red-600">{results.failed}</p>
                <p className="text-xs text-red-600">Failed</p>
              </div>
            </div>

            {/* Created Accounts */}
            {results.accounts.length > 0 && (
              <div>
                <h4 className="font-semibold text-slate-800 mb-2">Created Accounts</h4>
                <div className="bg-slate-50 rounded-xl overflow-hidden max-h-60 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 text-slate-600">Name</th>
                        <th className="text-left px-3 py-2 text-slate-600">Email</th>
                        <th className="text-left px-3 py-2 text-slate-600">Password</th>
                        <th className="text-center px-3 py-2 text-slate-600">Email</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {results.accounts.map((account, idx) => (
                        <tr key={account.email || idx} className="hover:bg-white">
                          <td className="px-3 py-2 text-slate-800">{account.full_name}</td>
                          <td className="px-3 py-2 text-slate-600">{account.email}</td>
                          <td className="px-3 py-2 font-mono text-slate-800">{account.password}</td>
                          <td className="px-3 py-2 text-center">
                            {account.email_sent ? (
                              <CheckCircle className="w-4 h-4 text-emerald-500 mx-auto" />
                            ) : (
                              <AlertCircle className="w-4 h-4 text-slate-400 mx-auto" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Save these credentials - they won't be shown again
                </p>
              </div>
            )}

            {/* Errors */}
            {results.errors.length > 0 && (
              <div>
                <h4 className="font-semibold text-red-600 mb-2">Errors</h4>
                <div className="bg-red-50 rounded-xl p-3 max-h-40 overflow-y-auto space-y-1">
                  {results.errors.map((error, idx) => (
                    <p key={`err-${error.row}-${idx}`} className="text-sm text-red-700">
                      Row {error.row}: {error.error}
                    </p>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => {
                  setResults(null);
                  setFile(null);
                }}
                className="px-5 py-2.5 text-blue-600 hover:bg-blue-50 rounded-xl transition"
              >
                Import More
              </button>
              <button
                onClick={onImported}
                className="px-5 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-t-2xl">
          <h3 className="text-xl font-bold text-white">Bulk Import PM Accounts</h3>
          <p className="text-sm text-white/80">Upload a CSV file to create multiple PM accounts</p>
        </div>
        
        <div className="p-6 space-y-5">
          {/* Template Download */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start justify-between">
              <div>
                <h4 className="font-semibold text-blue-800">Download Template</h4>
                <p className="text-sm text-blue-600 mt-1">Get the CSV template with correct headers</p>
              </div>
              <button
                onClick={downloadTemplate}
                disabled={!template}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                data-testid="download-template-btn"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
            </div>
            {template && (
              <div className="mt-3 text-xs text-blue-700">
                <p className="font-medium">Available Programs:</p>
                <p>{template.available_programs.join(', ') || 'No programs found'}</p>
              </div>
            )}
          </div>

          {/* File Upload */}
          <div
            className={`border-2 border-dashed rounded-xl p-8 text-center transition cursor-pointer ${
              dragActive 
                ? 'border-blue-500 bg-blue-50' 
                : file 
                  ? 'border-emerald-500 bg-emerald-50'
                  : 'border-slate-300 hover:border-slate-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
              data-testid="csv-file-input"
            />
            {file ? (
              <>
                <FileText className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <p className="font-medium text-emerald-700">{file.name}</p>
                <p className="text-sm text-emerald-600 mt-1">
                  {(file.size / 1024).toFixed(1)} KB
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                  className="mt-2 text-sm text-red-600 hover:underline"
                >
                  Remove file
                </button>
              </>
            ) : (
              <>
                <Upload className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                <p className="font-medium text-slate-700">Drop your CSV file here</p>
                <p className="text-sm text-slate-500 mt-1">or click to browse</p>
              </>
            )}
          </div>

          {/* Instructions */}
          <div className="text-xs text-slate-500 space-y-1">
            <p><strong>Required columns:</strong> full_name, email, program_name</p>
            <p><strong>Optional columns:</strong> phone, role (program_manager/location_manager)</p>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={onClose}
              className="px-5 py-2.5 text-slate-600 hover:bg-slate-100 rounded-xl transition"
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={loading || !file}
              className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-xl font-medium hover:shadow-lg transition disabled:opacity-50 flex items-center gap-2"
              data-testid="import-csv-btn"
            >
              {loading && <RefreshCw className="w-4 h-4 animate-spin" />}
              Import Accounts
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export {
  PostShiftForm, CreateProgramModal, CreateLocationModal, CreateShiftModal,
  AssignManagerModal, CreatePmAccountModal, BulkImportPmModal,
  EditProgramModal, EditLocationModal
};

function EditProgramModal({ program, onClose, onUpdated }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: program.name || '',
    description: program.description || '',
    service_type: program.service_type || 'residential_care',
    contact_email: program.contact_email || '',
    contact_phone: program.contact_phone || '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const vErr =
      validators.required(form.name) ||
      validators.companyName(form.name) ||
      validators.email(form.contact_email) ||
      validators.phone(form.contact_phone);
    if (vErr) { toast.error(vErr); return; }
    setLoading(true);
    try {
      await apiCall(`/api/employer/group-homes/programs/${program._id}`, {
        method: 'PUT',
        body: JSON.stringify(form),
      });
      toast.success('Program updated.');
      onUpdated();
    } catch (error) {
      toast.error(error.message || 'Failed to update program');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-xl font-bold text-slate-800">Edit Program</h3>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Program Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              maxLength={60}
              className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-indigo-500"
              required
              data-testid="edit-program-name-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-indigo-500 resize-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Service Type</label>
            <select
              value={form.service_type}
              onChange={(e) => setForm({ ...form, service_type: e.target.value })}
              className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-indigo-500"
            >
              <option value="residential_care">Residential Care</option>
              <option value="disability_support">Disability Support</option>
              <option value="behavioral_health">Behavioral Health</option>
              <option value="elderly_care">Elderly Care</option>
              <option value="rehabilitation">Rehabilitation</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Contact Email</label>
              <input
                type="email"
                value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Contact Phone</label>
              <input
                type="tel"
                value={form.contact_phone}
                onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-slate-600 hover:bg-slate-100 rounded-xl transition">Cancel</button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition disabled:opacity-50 flex items-center gap-2"
              data-testid="edit-program-save-btn"
            >
              {loading && <RefreshCw className="w-4 h-4 animate-spin" />}
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditLocationModal({ location, onClose, onUpdated }) {
  const [loading, setLoading] = useState(false);
  const addr = (location.address && typeof location.address === 'object') ? location.address : {};
  const [form, setForm] = useState({
    name: location.name || '',
    capacity: location.capacity || 10,
    address: { street: addr.street || '', city: addr.city || '', state: addr.state || '', zip: addr.zip || '' },
    contact_phone: location.contact_phone || '',
    contact_email: location.contact_email || '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const vErr =
      validators.required(form.name) ||
      validators.companyName(form.name) ||
      (!/\d/.test(form.address.street) || !/[a-zA-Z].*[a-zA-Z].*[a-zA-Z]/.test(form.address.street)
        ? 'Please enter a real street address (e.g., 123 Main St) — pick one from the suggestions.'
        : null) ||
      (form.address.zip && !/^\d{5}(-\d{4})?$/.test(form.address.zip.trim()) ? 'ZIP code must be 5 digits.' : null) ||
      validators.email(form.contact_email) ||
      validators.phone(form.contact_phone);
    if (vErr) { toast.error(vErr); return; }
    setLoading(true);
    try {
      await apiCall(`/api/employer/group-homes/locations/${location._id}`, {
        method: 'PUT',
        body: JSON.stringify(form),
      });
      toast.success('Group home updated.');
      onUpdated();
    } catch (error) {
      toast.error(error.message || 'Failed to update group home');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 sticky top-0 bg-white">
          <h3 className="text-xl font-bold text-slate-800">Edit Group Home</h3>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Location Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              maxLength={60}
              className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-emerald-500"
              required
              data-testid="edit-location-name-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Capacity (beds) *</label>
            <input
              type="number"
              value={form.capacity}
              onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) })}
              min="1" max="100"
              className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-emerald-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Address *</label>
            <div className="mb-2">
              <AddressAutocomplete
                value={form.address.street}
                onChange={(text) => setForm((prev) => ({ ...prev, address: { ...prev.address, street: text } }))}
                onSelect={(s) => setForm((prev) => ({
                  ...prev,
                  address: {
                    street: s.street || s.label,
                    city: s.city || prev.address.city,
                    state: s.state || prev.address.state,
                    zip: s.zip || prev.address.zip,
                  },
                }))}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-emerald-500"
                placeholder="Start typing street address…"
                required
                testId="edit-location-address"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <input type="text" value={form.address.city} placeholder="City" required
                onChange={(e) => setForm({ ...form, address: { ...form.address, city: e.target.value } })}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-emerald-500" />
              <input type="text" value={form.address.state} placeholder="State" required
                onChange={(e) => setForm({ ...form, address: { ...form.address, state: e.target.value } })}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-emerald-500" />
              <input type="text" value={form.address.zip} placeholder="ZIP" required
                onChange={(e) => setForm({ ...form, address: { ...form.address, zip: e.target.value } })}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-emerald-500" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Contact Phone</label>
              <input type="tel" value={form.contact_phone}
                onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-emerald-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Contact Email</label>
              <input type="email" value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-emerald-500" />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-slate-600 hover:bg-slate-100 rounded-xl transition">Cancel</button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition disabled:opacity-50 flex items-center gap-2"
              data-testid="edit-location-save-btn"
            >
              {loading && <RefreshCw className="w-4 h-4 animate-spin" />}
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
